/**
 * Browser file download and in-page print helpers.
 * Avoids window.open so downloads and print dialogs stay on the current page.
 */

export function sanitizeDownloadFilename(name: string, ext?: string): string {
  const base = name.replace(/[<>:"/\\|?*]+/g, '').trim() || 'download';
  if (!ext) return base;
  const normalizedExt = ext.startsWith('.') ? ext : `.${ext}`;
  return base.toLowerCase().endsWith(normalizedExt.toLowerCase()) ? base : `${base}${normalizedExt}`;
}

/** Build a Blob from binary bytes (avoids TS BlobPart / ArrayBufferLike mismatch). */
export function blobFromBytes(bytes: Uint8Array, type: string): Blob {
  return new Blob([Uint8Array.from(bytes)], { type });
}

export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function downloadBase64Attachment(
  data: string,
  filename: string,
  contentType = 'application/octet-stream',
): void {
  const byteString = atob(data);
  const bytes = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
  triggerBlobDownload(blobFromBytes(bytes, contentType), filename);
}

/**
 * Opens the browser print dialog for HTML content without navigating away.
 */
export function printHtmlDocument(html: string): void {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
  document.body.appendChild(iframe);

  const win = iframe.contentWindow;
  const doc = win?.document;
  if (!doc || !win) {
    iframe.remove();
    return;
  }

  doc.open();
  doc.write(html);
  doc.close();

  const cleanup = (): void => {
    iframe.remove();
  };
  win.addEventListener('afterprint', cleanup, { once: true });
  setTimeout(cleanup, 120_000);

  requestAnimationFrame(() => {
    win.focus();
    win.print();
  });
}
